/**
 * Orval custom mutator — axios instance wired for Zenith's auth model.
 *
 * backend ADR-0008 contract:
 *  - Access token is held IN MEMORY in the Pinia auth store (never in localStorage).
 *  - Refresh token lives in an httpOnly cookie; JS cannot read it.
 *  - On 401, call POST /refresh once (browser sends cookie automatically), store
 *    the new access token in memory, and retry the original request.
 *  - On refresh failure (another 401), clear auth state and redirect to /login.
 */
import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { useAuthStore } from "@/stores/auth";
import router from "@/router";
import { i18n } from "@/i18n";

const BASE_URL = import.meta.env.VITE_API_ENDPOINT ?? "http://localhost:4444";

export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  // Send the httpOnly refresh cookie on cross-origin requests (backend ADR-0008).
  withCredentials: true,
});

// Track in-flight refresh to avoid concurrent refresh storms.
let refreshPromise: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = axiosInstance
    .post<{ token?: { access_token?: string } }>("/refresh")
    .then((res) => {
      const token = res.data.token?.access_token;
      if (!token) throw new Error("no access token in refresh response");
      return token;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

axiosInstance.interceptors.request.use((config) => {
  // Lazily read the store so we don't cause circular init issues.
  const auth = useAuthStore();
  if (auth.accessToken) {
    config.headers["Authorization"] = `Bearer ${auth.accessToken}`;
  }
  // Tell the backend which locale to render user-facing text in (e.g. emails).
  // Kept in sync with the account locale by stores/auth.ts (fetchUser) and
  // stores/locale.ts (setLocale) — this always reflects the current choice.
  config.headers["Accept-Language"] = i18n.global.locale.value;
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // Never refresh-on-401 the auth endpoints themselves, or a failing /refresh
    // would recurse back into this same interceptor.
    const url = originalRequest?.url ?? "";
    const isAuthEndpoint = /\/(login|refresh|logout)$/.test(url);

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const newToken = await doRefresh();
        const auth = useAuthStore();
        auth.setAccessToken(newToken);

        // Retry the original request with the fresh token.
        if (originalRequest.headers) {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        }
        return axiosInstance(originalRequest);
      } catch {
        // Refresh failed — wipe auth state and send to login.
        const auth = useAuthStore();
        auth.clearAuth();
        router.push({ name: "login" });
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

/**
 * apiClient is the orval mutator entry-point.
 * orval calls: apiClient<T>(config) => Promise<T>
 */
export const apiClient = <T>(config: AxiosRequestConfig): Promise<T> => {
  return axiosInstance(config).then((res: AxiosResponse<T>) => res.data);
};

export default apiClient;
